import { useInfiniteQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { isEmpty } from 'lodash-es';
import { ElemasonFragmentProvider } from '../../../contexts/ElemasonFragmentContext';
import { Cell } from '../../../core/Cell';
import { useHaloScript, usePageFragment } from '../../../hooks';
import { useFragmentValue } from '../../../hooks/useFragmentValue';
import { queryKeys } from '../../../queryKeys';
import { GenericCarousel } from '../../containers/GenericCarousel';

import type {
  ElemasonReviewCarouselWidget,
  Nil,
  PaginatedResponse,
  ProductReview,
} from '@tectonic/types';
import { useMemo, type FC } from 'react';

interface ReviewCarouselWidgetProps {
  widget: ElemasonReviewCarouselWidget;
}

const PER_PAGE = 10;

const getPreviousPageParam = (
  response: PaginatedResponse<ProductReview>
): Nil<number> => {
  const { page } = response;
  return page <= 1 ? undefined : page - 1;
};

const ReviewCarouselWidget: FC<ReviewCarouselWidgetProps> = ({ widget }) => {
  const { data: wData, config: wConfig } = widget;

  const lFragment = usePageFragment(wData?.loader);
  const fragmentValue = useFragmentValue(lFragment);
  const reviewTag = useHaloScript(wData?.reviewTag);
  const maxReviewsToLoad = useHaloScript(wData?.maxReviewsToLoad);

  const params = { page: 1, perPage: PER_PAGE, tag: reviewTag };

  const getNextPageParam = (
    response: PaginatedResponse<ProductReview>
  ): Nil<number> => {
    const { page, found } = response;
    const count = page * PER_PAGE;
    return count < Math.min(found, maxReviewsToLoad ?? found)
      ? page + 1
      : undefined;
  };

  const {
    data,
    isError,
    isLoading,
    fetchNextPage: onLoadMore,
  } = useInfiniteQuery({
    getNextPageParam,
    getPreviousPageParam,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,

    queryKey: queryKeys.reviews(params),
    queryFn: ({ pageParam, signal }) => {
      const payload = {
        ...params,
        perPage: PER_PAGE,
        page: (pageParam ?? 1) as number,
      };

      return remixApi.getReviewsByTag(
        payload.tag!,
        payload.page,
        payload.perPage,
        signal
      );
    },
    initialPageParam: 1,
  });

  const hits = useMemo(
    () => data?.pages?.flatMap((page) => page.hits) ?? [],
    [data?.pages]
  );

  if (isError) {
    return null;
  }

  if (isLoading && lFragment) {
    return (
      <ElemasonFragmentProvider value={fragmentValue()}>
        {lFragment?.cells.map((cell) => <Cell key={cell.id} cell={cell} />)}
      </ElemasonFragmentProvider>
    );
  }

  if (isEmpty(hits)) {
    return null;
  }

  return (
    <GenericCarousel
      slides={hits!}
      fragment={wData?.slide}
      config={wConfig?.carousel}
      onLoadMore={wConfig?.disableAutoFetch ? undefined : onLoadMore}
    />
  );
};

ReviewCarouselWidget.displayName = 'ReviewCarouselWidget';

export { ReviewCarouselWidget };
